import { useCallback, useEffect, useRef, useState } from 'react';

export const AUTOSAVE_STATUS = {
  IDLE: 'IDLE',
  DIRTY: 'DIRTY',
  SAVING: 'SAVING',
  SAVED: 'SAVED',
  ERROR: 'ERROR',
};

function getErrorMessage(err, fallback = 'Erro ao sincronizar dados com o servidor.') {
  if (err?.response?.status === 409) {
    return 'Este registro foi alterado em outra sessão. Recarregue a página para atualizar.';
  }
  return (
    err?.response?.data?.message ||
    err?.response?.data?.mensagem ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
}

export function useAutosave({
  recordId,
  data,
  changeKey,
  getData,
  version,
  onSave,
  debounceMs = 2000,
  enabled = true,
  resetSavedStatusMs = 3000,
}) {
  const [status, setStatus] = useState(AUTOSAVE_STATUS.IDLE);
  const [errorMessage, setErrorMessage] = useState(null);

  const timeoutRef = useRef(null);
  const resetStatusTimeoutRef = useRef(null);
  const isSavingRef = useRef(false);

  // Ref para controlar se é a primeira carga do registro (evita salvar o estado inicial)
  const isFirstRender = useRef(true);

  // Mantém as props e valores mais recentes sempre atualizados
  const latestPropsRef = useRef({ getData, data, version, onSave, enabled, recordId });
  latestPropsRef.current = { getData, data, version, onSave, enabled, recordId };

  // Reseta estados e cancela timers ao trocar de prontuário/registro
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (resetStatusTimeoutRef.current) clearTimeout(resetStatusTimeoutRef.current);

    setStatus(AUTOSAVE_STATUS.IDLE);
    setErrorMessage(null);
    isFirstRender.current = true;
  }, [recordId]);

  const executeSave = useCallback(
    async (isManual = false) => {
      const {
        getData: currentGetData,
        data: currentDataProp,
        version: currentVersion,
        onSave: currentOnSave,
      } = latestPropsRef.current;

      if (isSavingRef.current || !currentOnSave) return;

      try {
        isSavingRef.current = true;
        setStatus(AUTOSAVE_STATUS.SAVING);
        setErrorMessage(null);

        const currentData =
          typeof currentGetData === 'function'
            ? currentGetData()
            : currentDataProp !== undefined
              ? currentDataProp
              : {};

        await currentOnSave({
          data: currentData,
          version: currentVersion,
          isManual,
        });

        setStatus(AUTOSAVE_STATUS.SAVED);

        if (resetStatusTimeoutRef.current) clearTimeout(resetStatusTimeoutRef.current);
        resetStatusTimeoutRef.current = setTimeout(() => {
          setStatus(AUTOSAVE_STATUS.IDLE);
        }, resetSavedStatusMs);
      } catch (err) {
        setStatus(AUTOSAVE_STATUS.ERROR);
        setErrorMessage(getErrorMessage(err));
      } finally {
        isSavingRef.current = false;
      }
    },
    [resetSavedStatusMs]
  );

  // Gatilho do debounce de autosave ao detectar mudança
  const effectiveChangeKey = changeKey !== undefined ? changeKey : data;

  useEffect(() => {
    if (!enabled || !recordId || effectiveChangeKey === undefined) return;

    // Se for a montagem inicial do registro, ignora o auto-save
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Sinaliza que há dados não salvos enquanto aguarda o debounce
    setStatus(AUTOSAVE_STATUS.DIRTY);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      executeSave(false);
    }, debounceMs);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [effectiveChangeKey, enabled, recordId, debounceMs, executeSave]);

  const triggerSave = useCallback(async () => {
    const { enabled: isEnabled, recordId: activeRecordId } = latestPropsRef.current;
    if (!isEnabled || !activeRecordId) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    await executeSave(true);
  }, [executeSave]);

  // Limpeza final ao desmontar o componente
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (resetStatusTimeoutRef.current) clearTimeout(resetStatusTimeoutRef.current);
    };
  }, []);

  return {
    status,
    errorMessage,
    manualSave: triggerSave,
    retrySave: triggerSave,
    triggerSave,
  };
}

export default useAutosave;
